import { type } from 'arktype';

export { shiftOverrideApprovalInfoSchema } from '@zag/core/models';
export type { ShiftOverrideApprovalInfo } from '@zag/core/models';

export const SHIFT_OVERRIDE_SUBJECT_TYPE = 'planning.shift-override';

export const SHIFT_OVERRIDE_APPROVE_PERMISSION = 'planning:shift-overrides:approve';

export const shiftOverrideApprovalPayloadSchema = type({
  planInstanceId: type('string').configure({
    description: 'ID of the plan instance containing the shift unit.',
    examples: ['planinst_abc123'],
  }),
  date: type('/^\\d{4}-\\d{2}-\\d{2}$/').configure({
    description: 'ISO 8601 calendar date (YYYY-MM-DD) the override applies to.',
    examples: ['2026-04-22'],
  }),
  shiftUnitId: type('string').configure({
    description: 'ID of the shift unit whose time range is overridden.',
    examples: ['shiftunit_abc123'],
  }),
  employeeId: type('string').configure({
    description: 'ID of the employee who owns the override.',
    examples: ['employee_abc123'],
  }),
  startTime: type('/^([01]\\d|2[0-3]):[0-5]\\d$/').configure({
    description: 'Requested start time (HH:mm).',
    examples: ['08:30'],
  }),
  endTime: type('/^([01]\\d|2[0-3]):[0-5]\\d$/').configure({
    description: 'Requested end time (HH:mm).',
    examples: ['16:30'],
  }),
  previousStartTime: type('/^([01]\\d|2[0-3]):[0-5]\\d$/ | null').configure({
    description: 'Start-time override in effect when the request was raised (HH:mm). Null when none.',
    examples: ['08:00'],
  }),
  previousEndTime: type('/^([01]\\d|2[0-3]):[0-5]\\d$/ | null').configure({
    description: 'End-time override in effect when the request was raised (HH:mm). Null when none.',
    examples: ['16:00'],
  }),
  'breakMinutes?': type('number.integer >= 0 | null').configure({
    description: 'Requested unpaid break in minutes. Null or absent when the break is not part of the request or the override is reset.',
    examples: [30],
  }),
  'previousBreakMinutes?': type('number.integer >= 0 | null').configure({
    description: 'Break override in effect when the request was raised, in minutes. Null when none.',
    examples: [15],
  }),
}).configure({ description: 'Approval-request payload for an employee-owned shift time-range and break override.' });

export type ShiftOverrideApprovalPayload = typeof shiftOverrideApprovalPayloadSchema.t;

export const markShiftQueuedResponseSchema = type({
  approvalRequestId: type('string').configure({
    description: 'ID of the approval request raised instead of writing the override directly.',
    examples: ['apr_abc123'],
  }),
}).configure({ description: 'Response when a shift time override was queued for approval.' });

export interface ShiftOverrideApprovalDetailContext {
  planInstanceId: string;
  shiftUnitId: string;
  shiftUnitName: string;
  employeeId: string;
  employeeName: string;
  date: string;
  currentStartTime: string;
  currentEndTime: string;
  requestedStartTime: string;
  requestedEndTime: string;
  currentBreakMinutes: number | null;
  requestedBreakMinutes: number | null;
}

export type MarkShiftQueuedResponse = typeof markShiftQueuedResponseSchema.t;
